import _ from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { isAndroid, isIOS } from "react-device-detect";
import { useColumnOrder, useExpanded, useFilters, useFlexLayout, useGroupBy, usePagination, useResizeColumns, useRowSelect, useRowState, useSortBy, useTable } from "react-table";
import { v4 } from "uuid";
import { GridToolbar } from "../../../GridToolbar/GridToolbar";
import { useExpandAllOnFirstRender } from "../../../Utils/useExpandAllOnFirstRender";
import { excludeUnselectableRows, shouldExcludeUnselectableRows } from "../../functions/excludeUnselectableRows";
import { getUnselectableRowIds } from "../../functions/getUnselectableRowIds";
import { getSelectedDataRows, triggerOnSelectedRowsChanged } from "../../functions/triggerOnSelectedRowsChanged";
import { useCallbackRef } from "../../utils/useCallbackRef";
import GridContainer from "../GridContainer";
import { convertColumnDefinitions, getCellDisplayValue } from "./DataGridColumnDefinition";
import { applyPreferences, getInitialSortBySettings, shouldUpdateColumnOrderPreference, shouldUpdateColumnWidthPreference, shouldUpdateSortByPreference, updateColumnOrderPreference, updateColumnWidthPreference, updateSortByPreference } from "./DataGridPreferences";
import DataGridTable from "./DataGridTable";
import { createDataGridRowContext } from "./DataRowRenderer";
import FixedHeader from "./FixedHeader";
/**Default column widths */
const defaultColumnWidths = { minWidth: 25, width: 200 };
DataGrid.defaultProps = {
    fixedColumnCount: 1,
    options: {
        allowSelection: false,
        allowMoveColumns: true,
        allowPagination: false,
        skipPageReset: true,
        rowsPerPageOptions: [5, 10, 25]
    },
    defaultCellDisplayValue: getCellDisplayValue,
    style: {
        grid: {
            height: "100%"
        },
        rowHeight: 40
    }
};
/**
 * Read-only Data Grid. Includes sorting, fixed grouping, pagination, row virtuailzation and selection.
 * @param props @see DataGridProps
 *
 */
export function DataGrid(props) {
    const { columnDefinitions, data, dataIsLoading, filters, options = {}, defaultCellDisplayValue, gridPreferences, onGridPreferencesChange, onRowOrderChangedForGridsWithoutGrouping: onRowOrderChanged, rowMetadata, 
    // Allows the grid to auto expand when the ID changes
    gridStateId } = props;
    const { getIsRowLoading, getIsRowReadOnly, getRowId, getRowProps } = rowMetadata;
    const rowHeight = props.style?.rowHeight ?? DataGrid.defaultProps.style.rowHeight;
    const gridStyle = { ...DataGrid.defaultProps.style.grid, ...props.style?.grid };
    const { allowMoveColumns, allowPagination, allowSelection, defaultSelectedRowIds = [], rowsPerPageOptions, selectionActions, onSelectedRowsChanged, resetSelectedRowsOnDataChange = false, skipPageReset } = {
        ...DataGrid.defaultProps.options,
        ...options
    };
    const diagonalScrollDisabled = isIOS || isAndroid;
    // internalGridPreferences represents the gridPreferences that the grid is aware of and gets updated when internal grid state is changed via interaction.
    // It is used to compare to props.gridPreferences when prop.gridPreferences changes to stop columns from being regenerated when props.gridPreferences get updated with changes that the grid has already applied.
    const [internalGridPreferences, setInternalGridPreferences] = useState(gridPreferences);
    const [columns, setColumns] = useState(convertColumnDefinitions(applyPreferences(columnDefinitions, gridPreferences?.columnPreferences), defaultCellDisplayValue));
    const [preferenceWidths, setPreferenceWidths] = useState({});
    const rootGridStateId = useRef(v4());
    //If no gridStateId is provided, use a root id so there will only be one state for the lifetime of the component.
    const uniqueGridStateId = gridStateId ? `${rootGridStateId.current},${gridStateId}` : rootGridStateId.current;
    useEffect(() => {
        setColumns(convertColumnDefinitions(applyPreferences(columnDefinitions, gridPreferences?.columnPreferences), defaultCellDisplayValue));
    }, [columnDefinitions]);
    //Stores the calculated widths on first render, to handle flex widths.  Not accurate after 1st render
    const [columnWidths, setColumnWidths] = useState({});
    const [previousUnselectableRowIds, setPreviousUnselectableRowIds] = useState([]);
    const [unselectableRowIds, setUnselectableRowIds] = useState([]);
    // #region React Table Hooks
    const reactTableProps = useTable({
        autoResetExpanded: false,
        autoResetSortBy: false,
        autoResetFilters: false,
        autoResetSelectedRows: resetSelectedRowsOnDataChange,
        columns,
        data,
        defaultCanFilter: true,
        defaultColumn: defaultColumnWidths,
        getRowId,
        autoResetPage: !skipPageReset,
        initialState: {
            selectedRowIds: defaultSelectedRowIds.reduce((acc, id) => {
                acc[id] = true;
                return acc;
            }, {}),
            groupBy: useMemo(() => columns.filter((col) => col.isGrouped == true).map((col) => col.id || ""), [columns]),
            hiddenColumns: useMemo(() => columns.filter((col) => col.isVisible == false).map((col) => col.id || ""), [columns])
        },
        stateReducer: (newState, action, prevState) => {
            if (action.type === "init" || action.type === "toggleRowSelected" || action.type === "toggleAllRowsSelected" || action.type === "excludeUnselectableRows") {
                const selectedRowIds = excludeUnselectableRows(newState.selectedRowIds, unselectableRowIds);
                setPreviousUnselectableRowIds(unselectableRowIds);
                return { ...newState, selectedRowIds: selectedRowIds };
            }
            return newState;
        },
        useControlledState: (state) => {
            // justification: type of state.columnResizing.columnWidths is defined by react-table not us
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const renderWidths = { ...columnWidths, ...preferenceWidths, ...state.columnResizing.columnWidths };
            return useMemo(() => ({
                ...state,
                //justification: state.columnResizing.columnWidths is an any type in React table types
                //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                headerWidths: renderWidths,
                columnResizing: {
                    ...state.columnResizing,
                    //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    columnWidths: renderWidths
                }
            }), [state, columnWidths, preferenceWidths]);
        }
    }, useFlexLayout, useColumnOrder, useFilters, useGroupBy, useSortBy, useExpanded, usePagination, useRowSelect, useRowState, useResizeColumns);
    const { headerGroups, prepareRow, rows, gotoPage, setPageSize, setAllFilters, state, getToggleAllRowsSelectedProps, getTableBodyProps, getTableProps, page, setColumnOrder, toggleAllRowsExpanded, toggleAllRowsSelected, toggleSortBy, dispatch: reactTableDispatch, visibleColumns, flatRows, expandedRows } = reactTableProps;
    // #endregion
    useExpandAllOnFirstRender(uniqueGridStateId, expandedRows, toggleAllRowsExpanded);
    useEffect(() => {
        setUnselectableRowIds(getUnselectableRowIds(data, getIsRowLoading, getIsRowReadOnly, getRowId));
    }, [data, getIsRowLoading, getIsRowReadOnly, getRowId]);
    useEffect(() => {
        if (shouldExcludeUnselectableRows(allowSelection, previousUnselectableRowIds, unselectableRowIds)) {
            //Dispatch custom action to trigger a refresh for the selected rows state as we want to exclude already selected items if they become unselectable
            reactTableDispatch({ type: "excludeUnselectableRows" });
        }
    }, [unselectableRowIds]);
    useEffect(() => {
        if (!_.isEqual(gridPreferences, internalGridPreferences)) {
            (gridPreferences?.columnPreferences || []).forEach((pref) => {
                const width = pref.width;
                if (width) {
                    setPreferenceWidths((prev) => ({ ...prev, [pref.columnId]: width }));
                }
            });
            setColumns(convertColumnDefinitions(applyPreferences(columnDefinitions, gridPreferences?.columnPreferences), defaultCellDisplayValue));
        }
        //This is needed because we do not reset state.sortBy on re-render.
        //Hence if the grid is initially rendered with no gridPreferences but later provided one, state.sortBy is not updated accordingly
        const initialSortByPreferences = getInitialSortBySettings(gridPreferences?.columnPreferences);
        //filter out preferences that are not on the grid
        const sortByPreferences = initialSortByPreferences.filter((p) => columns.some((c) => p.id === c.id));
        if (!_.isEqual(state.sortBy, sortByPreferences)) {
            sortByPreferences.map((sortingRule) => {
                toggleSortBy(sortingRule.id, sortingRule.desc, true);
            });
        }
    }, [gridPreferences]);
    //Identify actual column widths when columns change
    useLayoutEffect(() => {
        setColumnWidths((prevColumnWidths) => {
            const newColumnwidths = { ...prevColumnWidths };
            columns.forEach((column) => {
                const width = document.querySelector(`#header-${column.id}`)?.getBoundingClientRect()?.width;
                newColumnwidths[column.id] = column.pxWidth ?? width ?? defaultColumnWidths.width;
            });
            return newColumnwidths;
        });
    }, [columns]);
    const getColumnWidth = useCallback((columnId) => {
        return state.headerWidths[columnId];
    }, [state.headerWidths]);
    useEffect(() => {
        triggerOnSelectedRowsChanged(allowSelection, onSelectedRowsChanged, data, state.selectedRowIds, getRowId);
    }, [getRowId, state.selectedRowIds, allowSelection]);
    useEffect(() => {
        if (filters) {
            setAllFilters(filters);
        }
    }, [filters]);
    //Check to see if we care about updating grid preferences
    const shouldUpdateGridPreferences = () => {
        return !!(gridPreferences && gridPreferences?.columnPreferences && onGridPreferencesChange);
    };
    //Update column size preferences when columns are resized
    useEffect(() => {
        if (shouldUpdateGridPreferences() && shouldUpdateColumnWidthPreference(state.columnResizing)) {
            setInternalGridPreferences(updateColumnWidthPreference(gridPreferences, onGridPreferencesChange, state.columnResizing.columnWidths));
        }
    }, [state.columnResizing]);
    //Update column order preferences when column order is changed
    useEffect(() => {
        if (shouldUpdateGridPreferences() && shouldUpdateColumnOrderPreference(state.columnOrder, gridPreferences)) {
            setInternalGridPreferences(updateColumnOrderPreference(gridPreferences, onGridPreferencesChange, state.columnOrder));
        }
    }, [state.columnOrder]);
    //Update column sort by preferences when columns are sorted
    useEffect(() => {
        if (shouldUpdateGridPreferences() && shouldUpdateSortByPreference()) {
            setInternalGridPreferences(updateSortByPreference(gridPreferences, onGridPreferencesChange, state.sortBy));
        }
    }, [state.sortBy]);
    useEffect(() => {
        if (onRowOrderChanged) {
            const orderedRowIds = flatRows.map((row) => row.id);
            onRowOrderChanged(orderedRowIds);
        }
    }, [onRowOrderChanged, flatRows]);
    const numSelectedRows = getSelectedDataRows(data, state.selectedRowIds, getRowId).length;
    const [fixedHeadersRefCurrent, fixedHeadersRef] = useCallbackRef();
    //When column preferences are applied, the fixed columns are moved to the start of the array.
    const fixedColumnCount = columnDefinitions.filter((column) => column.fixed).length;
    const DataGridRowContext = createDataGridRowContext();
    const rowContext = useMemo(() => ({
        allowSelection,
        columns,
        fixedColumnCount,
        getColumnWidth,
        getRowProps,
        getIsRowLoading,
        getIsRowReadOnly,
        prepareRow
    }), [allowSelection, columns, fixedColumnCount, getColumnWidth, getIsRowLoading, getIsRowReadOnly, getRowProps, prepareRow]);
    // #region Renders the entire grid
    return (React.createElement(GridContainer, { paginationProps: {
            allowPagination: allowPagination,
            dataCount: data.length,
            page: state.pageIndex,
            rowCount: rows.length,
            rowsPerPage: state.pageSize,
            rowsPerPageOptions: rowsPerPageOptions,
            gotoPage: gotoPage,
            setPageSize: setPageSize
        }, style: gridStyle },
        React.createElement(GridToolbar, { numSelected: numSelectedRows, selectionActions: selectionActions, onClickDeselectRows: () => toggleAllRowsSelected(false) }, props.toolbar),
        React.createElement(DataGridRowContext.Provider, { value: rowContext },
            React.createElement("div", { style: { display: "flex", flexGrow: 2, flexDirection: "column", overflow: "hidden" } },
                React.createElement(FixedHeader, { allowSelection: allowSelection, fixedColumnCount: fixedColumnCount, fixedHeadersRef: fixedHeadersRef, getColumnWidth: getColumnWidth, getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps, headerGroups: headerGroups }),
                React.createElement(DataGridTable, { allowMoveColumns: allowMoveColumns, allowPagination: allowPagination, data: data, dataIsLoading: dataIsLoading, disableDiagonalScrolling: diagonalScrollDisabled, fixedColumnCount: fixedColumnCount, fixedHeadersElement: fixedHeadersRefCurrent, getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps, getTableBodyProps: getTableBodyProps, getTableProps: getTableProps, headerGroups: headerGroups, page: page, rowHeight: rowHeight, rows: rows, setColumnOrder: setColumnOrder, state: state, visibleColumns: visibleColumns })))));
    // #endregion
}
